// settings (catalog-outline.md "settings"; platform target model §5): per-key writes against the typed registry, never
// a wholesale replace (FIX of legacy device-config replace). Values travel in their registry wire form and are checked
// against the key's schema, scope, origin and permission by the server. Effective-dated keys
// (`business_day.start_time`) take effect from a future business day that core computes, never retroactively.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { AccountId, DeviceId, LocalDate, LocationId } from '../../primitives/codecs.ts';
import { ConfigConflictRecord } from '../../sync/protocol.ts';
import { CloudAuthoritative, ConfigFieldsWritten } from '../config/index.ts';
import { CatalogInvalid } from '../catalog/index.ts';

/** Every scope names the location the edit is made at (or for): the fact is recorded, synced and audited there. */
export const SettingScopeRef = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('account'), accountId: AccountId, locationId: LocationId }),
    z.object({ kind: z.literal('location'), locationId: LocationId }),
    z.object({ kind: z.literal('device'), locationId: LocationId, deviceId: DeviceId }),
  ])
  .meta({ id: 'SettingScopeRef' });

export const SettingSource = z.enum(['device', 'location', 'account', 'default']).meta({ id: 'SettingSource' });

export const SettingInvalid = defineError({
  code: 'settings.invalid', httpStatus: 422, message: 'errors.settings.invalid', retryable: false,
  details: z.object({
    key: z.string(),
    rule: z.string(),
    /** The units a refusal names (`units_still_on_cloud`: the location's units that have not reached their node yet). */
    units: z.array(z.object({ deviceId: z.string(), deviceName: z.string() })).max(64).optional(),
  }),
});

export const SettingUnknown = defineError({
  code: 'settings.unknown_key', httpStatus: 404, message: 'errors.settings.unknown_key', retryable: false,
  details: z.object({ key: z.string() }),
});

export const SetSetting = defineCommand({
  name: 'settings.set',
  summary: "Set one registry key at one scope; effective-dated keys take effect from the next business day core allows.",
  input: z.object({ scope: SettingScopeRef, key: z.string().min(1).max(100), value: z.unknown() }),
  output: z.object({ scope: SettingScopeRef, key: z.string(), value: z.unknown(), effectiveFrom: LocalDate.nullable() }),
  permission: { session: 'user' },
  risk: 'medium',
  availability: 'location',
  origin: 'any',
  emits: [ConfigFieldsWritten],
  errors: [SettingInvalid, SettingUnknown, CloudAuthoritative, CatalogInvalid],
  dryRun: true,
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['PUT /v1/location-settings/{id}', 'PUT /v1/devices/{id}', 'PATCH /v1/locations/{id}'],
});

export const ResetSetting = defineCommand({
  name: 'settings.reset',
  summary: 'Remove the value one scope sets for a key, so the next level (or the registry default) applies.',
  input: z.object({ scope: SettingScopeRef, key: z.string().min(1).max(100) }),
  output: z.object({ scope: SettingScopeRef, key: z.string(), reset: z.literal(true) }),
  permission: { session: 'user' },
  risk: 'medium',
  availability: 'location',
  origin: 'any',
  emits: [ConfigFieldsWritten],
  errors: [SettingInvalid, SettingUnknown, CloudAuthoritative],
  dryRun: true,
  agent: { expose: false },
});

export const EffectiveSettings = defineQuery({
  name: 'settings.effective',
  summary: 'Every registry key resolved for a location (and optionally one of its devices): device → location → account → default.',
  input: z.object({ locationId: LocationId, deviceId: DeviceId.optional(), asOf: LocalDate.optional() }),
  output: z.object({
    asOf: LocalDate,
    values: z.record(z.string(), z.object({ value: z.unknown(), source: SettingSource })),
  }),
  permission: { session: 'user' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['GET /v1/location-settings/{id}', 'GET /v1/location-config'],
});

export const ListConfigConflicts = defineQuery({
  name: 'sync.configConflicts.list',
  summary: 'Config fields edited at the node and in the cloud while they could not see each other, with the value kept.',
  input: z.object({ locationId: LocationId }),
  output: z.object({ conflicts: z.array(ConfigConflictRecord) }),
  permission: 'sync.review',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
});

export const settingsCommands = [SetSetting, ResetSetting] as const;
export const settingsQueries = [EffectiveSettings, ListConfigConflicts] as const;
